import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Button, Typography } from '@mui/material'
import { Computer, Fingerprint, WarningAmber } from '@mui/icons-material'
import { CopyButton } from '@/common/components'
import type { HostAddResponse } from '../../types'

interface HostDisplayStepProps {
  readonly host: HostAddResponse
  readonly onClose: () => void
}

export const HostDisplayStep: FC<HostDisplayStepProps> = ({ host, onClose }) => {
  const copyText = useMemo(
    () => `Host Name: ${host.hostName}\nHost ID: ${host.hostId}`,
    [host.hostName, host.hostId],
  )

  return (
    <Box sx={containerSx}>
      <Typography variant="h6" sx={titleSx}>
        Host ID Generated
      </Typography>

      <Alert severity="warning" icon={<WarningAmber />} sx={warningSx}>
        Save these details. You will need them to configure our daemon
      </Alert>

      <Box sx={infoSx}>
        <Box sx={infoHeaderSx}>
          <CopyButton text={copyText} tooltipText="Copy host details" />
        </Box>
        <Box sx={infoRowSx}>
          <Computer sx={infoIconSx} />
          <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
            Name
          </Typography>
          <Typography variant="body1" color="text.primary" sx={infoValueSx}>
            {host.hostName}
          </Typography>
        </Box>
        <Box sx={infoRowSx}>
          <Fingerprint sx={infoIconSx} />
          <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
            Host ID
          </Typography>
          <Typography variant="body1" color="text.primary" sx={infoValueMonoSx}>
            {host.hostId}
          </Typography>
        </Box>
      </Box>

      <Box sx={securityTipsSx}>
        <Typography variant="subtitle2" sx={tipsHeaderSx}>
          Security Tips
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Store the host ID securely - treat it like a password
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Never commit the host ID to version control
        </Typography>
      </Box>

      <Box sx={buttonContainerSx}>
        <Button variant="contained" onClick={onClose} sx={buttonSx}>
          Done
        </Button>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 2,
}

const warningSx: SxProps<Theme> = {
  mb: 3,
  alignItems: 'center',
}

const infoSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 1,
  bgcolor: 'action.hover',
  mb: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  position: 'relative',
}

const infoHeaderSx: SxProps<Theme> = {
  position: 'absolute',
  top: 8,
  right: 8,
}

const infoRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

const infoIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'primary.main',
}

const infoLabelSx: SxProps<Theme> = {
  fontWeight: 500,
  minWidth: 70,
}

const infoValueSx: SxProps<Theme> = {
  fontWeight: 400,
}

const infoValueMonoSx: SxProps<Theme> = {
  fontWeight: 400,
  fontFamily: 'monospace',
  wordBreak: 'break-all',
}

const securityTipsSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  mb: 3,
}

const tipsHeaderSx: SxProps<Theme> = {
  mb: 1,
}

const buttonContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 4,
}

const buttonSx: SxProps<Theme> = {
  minWidth: 120,
}
